import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAccessToken } from "../../../../contexts/AccessTokenContext";
import { useTranslation } from "react-i18next";
import { usePermission, useUser } from "../../../../hooks/usePermission";
import {
  Breadcrumb,
  notification,
  Row,
  Col,
  Spin,
  Typography,
  Form,
  DatePicker,
  Input,
  Select,
  Button,
  Table,
  Card,
} from "antd";
import { ArrowLeftOutlined, DeleteOutlined, DownOutlined, EditOutlined } from "@ant-design/icons";
import * as settings from "../../../../services/settings";
import * as companyService from "../../../../services/company";
import * as summaryReportService from "../../../../services/summaryreport";
import dayjs from "dayjs";

const defaultDateRange = [dayjs("2026-06-01"), dayjs("2026-06-30")];

const SummaryReportDetail = () => {
  const [params] = useSearchParams();
  const type = params.get("type");
  const container = params.get("container");
  const [loading, setLoading] = useState(false);
  const { accessToken } = useAccessToken();
  const hasFetchedRef = useRef(false);
  const { companyId: userCompanyId } = useUser();
  const { hasPermission, isSuperuser, groups } = usePermission();
  const { t, i18n } = useTranslation(["summaryreport", "buttons", "common", "menus"]);
  const { Title, Link } = Typography;
  const { RangePicker } = DatePicker;
  const [from] = Form.useForm();
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [companyList, setCompanyList] = useState([]);
  const [mappingCompanyId, setMappingCompanyId] = useState(userCompanyId);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectRowItem, setSelectRowItem] = useState(null);

  const itemsBreadcrumb = [
    { title: t("Page_Body_Tab_Menu.Home", { ns: "menus" }) },
    { title: t("Page_Body_Tab_Menu.Menu", { ns: "menus" }) },
    {
      title: <a href={`/summary/report/${type}`}>{t("Title", { type: type })}</a>,
    },
    {
      title: (
        <a
          href={`/summary/report/detail?type=${type}&container=${container}`}
          style={{ fontWeight: 600, color: "black" }}
        >
          {t("Title_Detail", { type: type })}
        </a>
      ),
    },
  ];

  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, message, description) => {
      api[type]({ message, description });
    },
    [api]
  );

  const initialValues = {
    reference: undefined,
    origin_transportation: container,
    company_id: undefined,
    customer_id: undefined,
  };

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys,

    onChange: (selectedKeys) => {
      // เอาเฉพาะตัวล่าสุดที่เลือก
      const item = selectedKeys.slice(-1);
      setSelectedRowKeys(item);
      setSelectRowItem(item[0]);
    },
  };

  const columns = [
    {
      title: "ODR",
      dataIndex: "odr",
    },
    {
      title: "Warehouse Time",
      dataIndex: "warehouse_time",
    },
    {
      title: "Filter Date",
      dataIndex: "filter_date",
      render: (_, record) => {
        const filter_date = record.filter_date != null ? dayjs(record.filter_date).format("YYYY-MM-DD HH:mm:ss") : "-";
        return <p>{filter_date}</p>;
      },
    },
    {
      title: "Depart At",
      dataIndex: "depart_at",
      render: (_, record) => {
        const depart_at = record.depart_at != null ? dayjs(record.depart_at).format("YYYY-MM-DD HH:mm:ss") : "-";
        return <p>{depart_at}</p>;
      },
    },
    {
      title: "Expect Arrive At",
      dataIndex: "expect_arrive_at",
      render: (_, record) => {
        const expect_arrive_at =
          record.expect_arrive_at != null ? dayjs(record.expect_arrive_at).format("YYYY-MM-DD HH:mm:ss") : "-";
        return <p>{expect_arrive_at}</p>;
      },
    },
    {
      title: "Reference",
      dataIndex: "reference",
    },
    {
      title: "Bill No",
      dataIndex: "bill_no",
    },
    {
      title: "Origin Transportation",
      dataIndex: "origin_transportation",
    },
    {
      title: "Route",
      dataIndex: "route",
    },
    {
      title: "Company Code",
      dataIndex: "company_code",
    },
    {
      title: "Customer Code",
      dataIndex: "customer_code",
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Type",
      dataIndex: "type",
    },
    {
      title: "Origin",
      dataIndex: "origin",
    },
    {
      title: "Total Count",
      dataIndex: "total_count",
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
    },
    {
      title: "Package Weight",
      dataIndex: "package_weight",
    },
    {
      title: "Total Weight",
      dataIndex: "total_weight",
    },
    {
      title: "Package Length",
      dataIndex: "package_length",
    },
    {
      title: "Package Width",
      dataIndex: "package_width",
    },
    {
      title: "Package Height",
      dataIndex: "package_height",
    },
    {
      title: "Package Volume",
      dataIndex: "package_volume",
    },
    {
      title: "Total Volume",
      dataIndex: "total_volume",
    },
    {
      title: "Density",
      dataIndex: "density",
    },
    {
      title: "Price Volume Comp",
      dataIndex: "price_volume_comp",
      render: (_, record) => {
        const text = record?.price_volume_comp ?? "-";
        return <p>{text}</p>;
      },
    },
    {
      title: "Price Volume Cust",
      dataIndex: "price_volume_cust",
      render: (_, record) => {
        const text = record?.price_volume_cust ?? "-";
        return <p>{text}</p>;
      },
    },
    {
      title: "Price Volume Comp",
      dataIndex: "price_volume_comp",
      render: (_, record) => {
        const text = record?.price_volume_comp ?? "-";
        return <p>{text}</p>;
      },
    },
    {
      title: "Price Weight Cust",
      dataIndex: "price_weight_cust",
      render: (_, record) => {
        const text = record?.price_weight_cust ?? "-";
        return <p>{text}</p>;
      },
    },
    {
      title: "Price Weight Comp",
      dataIndex: "price_weight_comp",
      render: (_, record) => {
        const text = record?.price_weight_comp ?? "-";
        return <p>{text}</p>;
      },
    },
    {
      title: "Company Price",
      dataIndex: "company_price",
    },
    {
      title: "Customer Price",
      dataIndex: "customer_price",
    },
    {
      title: "Company Cost",
      dataIndex: "company_cost",
    },
    {
      title: "Customer Cost",
      dataIndex: "customer_cost",
    },
    {
      title: "Company Bill",
      dataIndex: "company_bill",
    },
    {
      title: "Customer Bill",
      dataIndex: "customer_bill",
    },
    {
      title: "Origin Delivery",
      dataIndex: "origin_delivery",
    },
    {
      title: "Wooden Crate",
      dataIndex: "wooden_crate",
    },
    {
      title: "Insurance",
      dataIndex: "insurance",
    },
    {
      title: "Thai Delivery",
      dataIndex: "thai_delivery",
    },
    {
      title: "Total Received",
      dataIndex: "total_received",
    },
    {
      title: "Bill Status",
      dataIndex: "bill_status",
    },
    {
      title: "Company Weight",
      dataIndex: "company_weight",
    },
    {
      title: "Customer Weight",
      dataIndex: "customer_weight",
    },
    {
      title: "Company Volume",
      dataIndex: "company_volume",
    },
    {
      title: "Customer Volume",
      dataIndex: "customer_volume",
    },
  ];

  const onFinish = async () => {
    const values = from.getFieldsValue();

    try {
      const newValue = {
        date_from: dateRange[0].format("YYYY-MM-DD"),
        date_to: dateRange[1].format("YYYY-MM-DD"),
        route: type,
        origin_transportation: container,
        ...values,
      };

      setLoading(true);
      await fetchSummaryReportDetail(newValue);
    } finally {
      setLoading(false);
    }
  };

  const onReset = async () => {
    from.resetFields();

    try {
      setLoading(true);
      await fetchSummaryReportDetail({
        route: type,
        origin_transportation: container,
        // date_from: defaultDateRange[0].format("YYYY-MM-DD"),
        // date_to: defaultDateRange[1].format("YYYY-MM-DD"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const disableFutureDates = (current) => {
    return current && current.isAfter(dayjs().endOf("day"));
  };

  const getCompanyList = async () => {
    setLoading(true);
    try {
      const companyData = await companyService.getCompanysList(accessToken);
      setCompanyList(companyData);
    } catch (err) {
      const { message } = settings.getErrorMessage(err);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const mappingSelectOption = async () => {
    if (isSuperuser || userCompanyId === null) {
      await getCompanyList();
    } else {
      await getCompanyById(userCompanyId);
    }
  };

  const optionCompanySelection = (companies = []) => {
    const hasMultiple = companies.length > 1;

    return [
      ...(hasMultiple ? [{ label: t("Search.All_Company", { defaultValue: "All Company" }), value: "all" }] : []),
      ...companies.map((item, index) => ({
        label: `${item.company_name} (${item.company_code})`,
        value: item.id,
        key: `company-${index}-${item.id}`,
      })),
    ];
  };

  const optionCustomerSelection = (companies = []) => {
    return [
      { label: t("Search.All_Customer", { defaultValue: "All Customer" }), value: "all" },
      ...companies
        .filter((company) => company.id === mappingCompanyId)
        .flatMap((company) =>
          (company.app_customer || []).map((customer, cIndex) => ({
            label: `${customer.id} (${customer.customer_name}-${customer.customer_code})`,
            value: customer.customer_code,
            key: `customer-${cIndex}-${customer.id}`,
          }))
        ),
    ];
  };

  // Filter `option.label` match the user type `input`
  const filterOption = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const handleChangeCompany = (value, _) => {
    setMappingCompanyId(value);
  };

  const fetchSummaryReportDetail = async (params) => {
    setLoading(true);
    try {
      const resp = await summaryReportService.getSummaryReportDetail(accessToken, params);
      if (resp) {
        setData(resp);
      }
    } catch (error) {
      console.error("error:", error);
      const { message } = settings.getErrorMessage(error);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportSummaryReportDetail = async () => {
    try {
      setLoading(true);

      const params = {
        origin_transportation: container,
        // reference: selectRowItem,
        route: type,
      };
      const response = await summaryReportService.exportExcelSummaryReportDetail(accessToken, params);

      const dateformat = dayjs().format("YYYYDDMM");
      let filename = `Cargo Cost per CNT_Route Type ${type}_detail${dateformat}.zip`;

      await exportExcel(response, filename);

      setSelectRowItem(null);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error("error:", error);
      const { message } = settings.getErrorMessage(error);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async (response, filename) => {
    try {
      const contentDisposition = response.headers["content-disposition"] || "";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      const contentType = response.headers["content-type"] || "";
      const isZip = contentType.includes("application/zip");
      const fallbackExt = isZip ? ".zip" : ".csv";

      const blob = new Blob([response.data], { type: contentType || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);

      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      openNotificationWithIcon(
        settings.NotificationType.SUCCESS,
        t("Success", { ns: "common" }),
        t("Export_Success", { ns: "common" }) || "Export completed successfully."
      );
    } catch (error) {
      console.error("error:", error);
      const { message } = settings.getErrorMessage(error);
      openNotificationWithIcon(settings.NotificationType.ERROR, t("Error", { ns: "common" }), `${message}`);
    }
  };

  useEffect(() => {
    if (!accessToken || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    (async () => {
      await Promise.all([
        fetchSummaryReportDetail({
          route: type,
          // date_from: defaultDateRange[0].format("YYYY-MM-DD"),
          // date_to: defaultDateRange[1].format("YYYY-MM-DD"),
          origin_transportation: container,
        }),
        mappingSelectOption(),
      ]);
    })();
  }, [accessToken, type, container]);

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row flex={1}>
          <Col span={24}>
            <Breadcrumb items={itemsBreadcrumb} />
            <Title level={4} style={{ textWrap: "nowrap", display: "flex", gap: "7px", padding: "15px 0px" }}>
              <ArrowLeftOutlined style={{ fontSize: "20px" }} />
              {t("Title_Detail", { type: type })}
            </Title>
          </Col>
        </Row>
        {/* Form search */}
        <Row style={{ display: "flex", justifyContent: "left", overflowX: "auto" }}>
          <Col span={12}>
            <Form
              form={from}
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              labelAlign="left"
              onFinish={onFinish}
              layout="horizontal"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  from.submit();
                }
              }}
              initialValues={initialValues}
            >
              {/* <Form.Item label={t("Form.Depart_At")}>
                <RangePicker
                  allowEmpty
                  value={dateRange}
                  style={{ width: "100%", minWidth: 200 }}
                  onChange={handleDateChange}
                  disabledDate={disableFutureDates}
                ></RangePicker>
              </Form.Item> */}
              <Form.Item label={t("Form.Container")} name="origin_transportation" value={container}>
                <Input size="default" placeholder="Container" disabled={true}></Input>
              </Form.Item>
              {/* <Form.Item label={t("Form.Search")} name="reference">
                <Input size="default" placeholder="Reference"></Input>
              </Form.Item>
              <Form.Item label={t("Form.Company")} name="company_code" key="company_code">
                <Select
                  style={{ width: "100%", minWidth: 200 }}
                  showSearch
                  placeholder={t("Please_Select", { ns: "common" })}
                  options={optionCompanySelection(companyList)}
                  filterOption={filterOption}
                  onSelect={handleChangeCompany}
                ></Select>
              </Form.Item>
              <Form.Item label={t("Form.Customer")} name="customer_id" key="customer_id">
                <Select
                  style={{ width: "100%", minWidth: 200 }}
                  showSearch
                  placeholder={t("Please_Select", { ns: "common" })}
                  allowClear
                  options={optionCustomerSelection(companyList)}
                  filterOption={filterOption}
                ></Select>
              </Form.Item>
              <Row justify="end" align="middle">
                <Col span={24} style={{ textAlign: `end` }}>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>
                      {" "}
                      {t("BTN_Search", { ns: "buttons" })}
                    </Button>
                    <Button type="default" onClick={() => onReset()} style={{ marginLeft: "5px" }} disabled={loading}>
                      {" "}
                      {t("BTN_Reset", { ns: "buttons" })}
                    </Button>
                  </Form.Item>
                </Col>
              </Row> */}
            </Form>
          </Col>
        </Row>

        {/* Button for export excel */}
        <div className="d-flex justify-items-start pb-5">
          <Row flex={1}>
            <Col span={24}>
              <Button type="primary" className="mx-0.5" onClick={exportSummaryReportDetail}>
                Export Summary Report Detail
              </Button>
            </Col>
          </Row>
        </div>

        {/* Table */}
        <Row>
          <Col span={24}>
            <Card>
              <Table
                loading={loading}
                scroll={{ x: `max-content` }}
                columns={columns}
                dataSource={data}
                rowKey={(record) => record.reference}
                // rowSelection={rowSelection}
                pagination={{
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
              ></Table>
            </Card>
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export default SummaryReportDetail;
